"use client";

import { MarketConfiguration } from "@/config/markets/const";
import type { CustomProduct } from "@apptus/esales-api";
import type { CartItem } from "@repo/core";
import { tv } from "tailwind-variants";
import {
  type StockProps,
  type StockStatus,
  StockStatusEnum,
} from "./Stock.types";

export const stockStatusVariants = tv({
  slots: {
    root: "inline-flex items-center gap-2 self-center",
    trigger: "inline-flex items-center underline cursor-pointer py-0",
    status: "inline-block",
    explanation: "text-sm text-gray-500",
    indicator: "inline-flex h-1.5 w-1.5 shrink-0 rounded-full",
  },
  variants: {
    kind: {
      accordion: "group gap-1 whitespace-nowrap",
      dialog: "text-black",
    },
    type: {
      pdp: {
        root: "flex-wrap",
      },
      plp: {},
      custom: {},
      cart: {},
      checkout: {},
    },
    status: {
      [StockStatusEnum.UNKNOWN]: { indicator: "bg-yellow-600" },
      [StockStatusEnum.IN_STOCK_ITEM]: { indicator: "bg-green-600 text-white" },
      [StockStatusEnum.DELIVERY_ITEM]: {
        indicator: "hidden bg-green-600 text-transparent",
      },
      [StockStatusEnum.INCOMING_ITEM]: { indicator: "bg-green-600 text-white" },
      [StockStatusEnum.SOLD_OUT_ITEM]: {
        indicator: "bg-muted-foreground text-transparent",
      },
      [StockStatusEnum.OUT_OF_STOCK_ITEM]: {
        indicator: "bg-muted-foreground text-transparent",
      },
      [StockStatusEnum.LIMITED_STOCK_ITEM]: {
        indicator: "bg-green-600 text-white",
      },
    },
  },
});

export const NOT_OVERSELLABLE_ARTICLE_STATUSES = [
  "out_of_stock_at_supplier",
  "terminated_collaboration_sell",
  "discontinued_item",
  "limited_keep",
  "time_limited_keep",
  "stock_item",
];

export const OVERSELLABLE_ARTICLE_STATUSES = ["backorder_item"];

export const getMaxPurchaseQuantity = (
  item?: CustomProduct | CartItem,
  current?: CartItem,
) => {
  if (!item) {
    return Infinity;
  }

  if ("typedCustom" in item) {
    const productIsOversellable = item.custom?.variantFlags?.some((attr) =>
      OVERSELLABLE_ARTICLE_STATUSES.includes(attr.id),
    );

    if (productIsOversellable) {
      return Infinity;
    }

    let stock = item.typedCustom?.numbers.stockOnHand;

    /**
     * @TODO
     * TEMPORARY SOLUTION, WE NEED TO GET NEXT DELIVERY QUANTITY ALSO
     */
    const stockStatus = item.custom?.stockStatus?.[0]?.id;

    if (stockStatus === "Incoming") {
      return Infinity;
    }

    if (typeof stock === "number") {
      if (current?.quantity) stock -= current.quantity;

      return Math.max(stock, 0);
    }

    return Infinity;
  }

  if ("extras" in item) {
    if (!item.extras?.stock_status) {
      return Infinity;
    }

    const productIsOversellable = OVERSELLABLE_ARTICLE_STATUSES.includes(
      item.extras?.stock_status,
    );

    if (productIsOversellable) {
      return Infinity;
    }

    const stock = item.stockStatus?.quantity;

    return !!stock && stock > 0 ? stock : Infinity;
  }

  return Infinity;
};

export const isProductPurchasable = (
  product?: CustomProduct,
  item?: CartItem,
) => {
  if (!product) {
    return false;
  }

  const maxQuantity = getMaxPurchaseQuantity(product) - (item?.quantity ?? 0);

  return !(maxQuantity <= 0);
};

export const isProductOversellable = (product?: CustomProduct) => {
  if (!product) {
    return false;
  }

  const productHasStockOnHand = Boolean(
    product.typedCustom?.numbers?.stockOnHand === undefined ||
    product.typedCustom?.numbers?.stockOnHand > 0,
  );
  const productIsOversellable = product.custom?.variantFlags?.some((attr) =>
    OVERSELLABLE_ARTICLE_STATUSES.includes(attr.id),
  );
  const productIsNotOversellable = product.custom?.statuses?.some((status) =>
    NOT_OVERSELLABLE_ARTICLE_STATUSES.includes(status.id),
  );

  if (productIsOversellable === true) {
    return true;
  }

  if (productIsNotOversellable === true || productHasStockOnHand === false) {
    return false;
  }

  return true;
};

export const isItemOversellable = (item: CartItem) => {
  if (!item.extras?.stock_status) return false;

  return OVERSELLABLE_ARTICLE_STATUSES.includes(item.extras.stock_status);
};

export function getStockStatus({
  item,
  type,
  market,
}: StockProps & { market: MarketConfiguration }): StockStatus | undefined {
  if (!item) return;
  const formatter = new Intl.DateTimeFormat(market.locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  switch (type) {
    case "cart":
    case "checkout": {
      const { stockStatus, price } = item;
      switch (stockStatus?.statusCode) {
        case "StockItem": {
          const { quantity } = stockStatus;
          if (typeof quantity === "number") {
            if (quantity > 0) {
              return {
                code: StockStatusEnum.IN_STOCK_ITEM,
                quantity: quantity,
                pretty: quantity.toString(),
              };
            }
            return {
              code: StockStatusEnum.SOLD_OUT_ITEM,
            };
          }
          return {
            code: StockStatusEnum.IN_STOCK_ITEM,
            quantity: Infinity,
            pretty: "10+",
          };
        }
        case "DeliveryItem": {
          const { nextDelivery /* deliveryTimeDays */ } = stockStatus;
          if (nextDelivery?.date && nextDelivery?.quantity) {
            const date = new Date(nextDelivery.date);
            if (date.getTime() > Date.now()) {
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                quantity: nextDelivery.quantity,
                pretty: nextDelivery.quantity.toString(),
                date: formatter.format(date),
              };
            } else {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                quantity: nextDelivery.quantity,
                pretty: nextDelivery.quantity.toString(),
                date: formatter.format(tomorrow),
              };
            }
          }
          /**
           * We might want to keep this in case we need delivery estimates in cart/checkout
           *
           * if (deliveryTimeDays) {
           *     const date = new Date();
           *     date.setDate(date.getDate() + deliveryTime);
           *     return {
           *         code: StockStatusEnum.DELIVERY_ITEM,
           *         date: formatter.format(date),
           *     };
           * }
           *
           */
          return {
            code: StockStatusEnum.DELIVERY_ITEM,
          };
        }
        case "OutOfStock": {
          return {
            code: StockStatusEnum.OUT_OF_STOCK_ITEM,
          };
        }
        case "PendingSoldOut": {
          const { quantity } = stockStatus;
          if (typeof quantity === "number") {
            if (quantity > 0) {
              return {
                code: StockStatusEnum.LIMITED_STOCK_ITEM,
                quantity: quantity,
                pretty: quantity.toString(),
              };
            }
            return {
              code: StockStatusEnum.OUT_OF_STOCK_ITEM,
            };
          }
          return {
            code: StockStatusEnum.LIMITED_STOCK_ITEM,
            quantity: Infinity,
            pretty: "10+",
          };
        }
        case "SoldOut": {
          return {
            code: StockStatusEnum.SOLD_OUT_ITEM,
          };
        }
        /** For GWP */
        case "Unknown": {
          if (price <= 0) {
            return {
              code: StockStatusEnum.IN_STOCK_ITEM,
              quantity: Infinity,
              pretty: "10+",
            };
          }
          return {
            code: StockStatusEnum.UNKNOWN,
          };
        }
        default: {
          return {
            code: StockStatusEnum.UNKNOWN,
          };
        }
      }
    }
    case "pdp": {
      const [stockStatus] = item.custom?.stockStatus ?? [];
      const [nextDeliveryDate] = item.custom?.nextDeliveryDate ?? [];
      const stockOnHand = item.typedCustom?.numbers.stockOnHand;
      const deliveryTimeDays = item.typedCustom?.numbers.delivery_time_days;

      switch (stockStatus?.id) {
        case "StockItem": {
          const deliveryDate = new Date();
          if (deliveryTimeDays) {
            deliveryDate.setDate(deliveryDate.getDate() + deliveryTimeDays);
          }
          if (typeof stockOnHand === "number") {
            if (stockOnHand > 0) {
              return {
                code: StockStatusEnum.IN_STOCK_ITEM,
                date: formatter.format(deliveryDate),
                quantity: stockOnHand,
                pretty: stockOnHand.toString(),
              };
            }
            return {
              code: StockStatusEnum.SOLD_OUT_ITEM,
            };
          }
          return {
            code: StockStatusEnum.IN_STOCK_ITEM,
            quantity: Infinity,
            date: formatter.format(deliveryDate),
            pretty: "10+",
          };
        }
        case "DeliveryItem": {
          if (nextDeliveryDate?.id) {
            const date = new Date(nextDeliveryDate.id);
            if (date.getTime() > Date.now()) {
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                date: formatter.format(date),
              };
            } else {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                date: formatter.format(tomorrow),
              };
            }
          }
          if (deliveryTimeDays) {
            const date = new Date();
            date.setDate(date.getDate() + deliveryTimeDays);
            return {
              code: StockStatusEnum.DELIVERY_ITEM,
              date: formatter.format(date),
            };
          }
          return {
            code: StockStatusEnum.DELIVERY_ITEM,
          };
        }
        case "OutOfStock": {
          if (nextDeliveryDate?.id) {
            const date = new Date(nextDeliveryDate.id);
            if (date.getTime() > Date.now()) {
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                date: formatter.format(date),
              };
            } else {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                date: formatter.format(tomorrow),
              };
            }
          }
          return {
            code: StockStatusEnum.OUT_OF_STOCK_ITEM,
          };
        }
        case "PendingSoldOut": {
          if (typeof stockOnHand === "number") {
            if (stockOnHand > 0) {
              return {
                code: StockStatusEnum.LIMITED_STOCK_ITEM,
                quantity: stockOnHand,
                pretty: stockOnHand.toString(),
              };
            }
            return {
              code: StockStatusEnum.OUT_OF_STOCK_ITEM,
            };
          }
          return {
            code: StockStatusEnum.LIMITED_STOCK_ITEM,
            quantity: Infinity,
            pretty: "10+",
          };
        }
        case "Incoming": {
          if (nextDeliveryDate?.id) {
            const date = new Date(nextDeliveryDate.id);
            if (date.getTime() > Date.now()) {
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                date: formatter.format(date),
              };
            } else {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return {
                code: StockStatusEnum.INCOMING_ITEM,
                date: formatter.format(tomorrow),
              };
            }
          }
          return {
            code: StockStatusEnum.UNKNOWN,
          };
        }
        case "SoldOut": {
          return {
            code: StockStatusEnum.SOLD_OUT_ITEM,
          };
        }
        default: {
          return {
            code: StockStatusEnum.UNKNOWN,
          };
        }
      }
    }
    case "plp": {
      const stockStatus = item.custom?.stockStatus?.[0]?.id;
      switch (stockStatus) {
        case "StockItem": {
          return {
            code: StockStatusEnum.IN_STOCK_ITEM,
            quantity: Infinity,
            pretty: "10+",
          };
        }
        case "DeliveryItem": {
          return {
            code: StockStatusEnum.DELIVERY_ITEM,
          };
        }
        case "Incoming": {
          return {
            code: StockStatusEnum.INCOMING_ITEM,
          };
        }
        case "OutOfStock": {
          return {
            code: StockStatusEnum.OUT_OF_STOCK_ITEM,
          };
        }
        case "PendingSoldOut": {
          return {
            code: StockStatusEnum.LIMITED_STOCK_ITEM,
            quantity: Infinity,
            pretty: "10+",
          };
        }
        case "SoldOut": {
          return {
            code: StockStatusEnum.SOLD_OUT_ITEM,
          };
        }
        default: {
          return {
            code: StockStatusEnum.UNKNOWN,
          };
        }
      }
    }
    default: {
      return {
        code: StockStatusEnum.UNKNOWN,
      };
    }
  }
}
